import { api } from './api';

export interface Client {
  _id: string;
  firstName: string;
  lastName: string;
  fullName: string;
  initials: string;
  displayName?: string;
  company?: string;
  jobTitle?: string;
  contact: {
    email: string;
    phone?: string;
    secondaryEmail?: string;
    secondaryPhone?: string;
    preferredContact: 'email' | 'phone' | 'text';
  };
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  socialLinks?: {
    website?: string;
    instagram?: string;
    linkedin?: string;
    facebook?: string;
    tiktok?: string;
    twitter?: string;
    youtube?: string;
  };
  timezone?: string;
  status: 'active' | 'inactive' | 'lead' | 'archived';
  source?: string;
  sourceDetails?: string;
  tags: string[];
  notes?: ClientNote[];
  defaultHourlyRate?: number;
  currency?: string;
  avatarUrl?: string;
  avatarColor?: string;
  lastContactedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ClientNote {
  _id: string;
  content: string;
  createdBy: {
    _id: string;
    profile: {
      firstName: string;
      lastName: string;
    };
  };
  createdAt: string;
  updatedAt: string;
}

export interface ClientsListParams {
  page?: number;
  limit?: number;
  search?: string;
  status?: 'active' | 'inactive' | 'lead' | 'archived' | 'all';
  tags?: string;
  sortBy?: 'name' | 'email' | 'company' | 'createdAt' | 'lastContactedAt';
  sortOrder?: 'asc' | 'desc';
}

export interface ClientsListResponse {
  clients: Client[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasMore: boolean;
  };
}

export interface ClientStats {
  total: number;
  active: number;
  inactive: number;
  leads: number;
  archived: number;
}

export type CreateClientData = {
  firstName: string;
  lastName: string;
  displayName?: string;
  company?: string;
  jobTitle?: string;
  contact: {
    email: string;
    phone?: string;
    secondaryEmail?: string;
    secondaryPhone?: string;
    preferredContact?: 'email' | 'phone' | 'text';
  };
  address?: {
    street?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  socialLinks?: {
    website?: string;
    instagram?: string;
    linkedin?: string;
    facebook?: string;
    tiktok?: string;
    twitter?: string;
    youtube?: string;
  };
  timezone?: string;
  status?: 'active' | 'inactive' | 'lead' | 'archived';
  source?: string;
  sourceDetails?: string;
  tags?: string[];
  defaultHourlyRate?: number;
  currency?: string;
};

export type UpdateClientData = Partial<CreateClientData>;

// API Functions
export const clientsApi = {
  // List clients
  list: async (params: ClientsListParams = {}): Promise<ClientsListResponse> => {
    const response = await api.get('/clients', { params });
    return response.data.data;
  },

  // Get single client
  get: async (id: string): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    return response.data.data.client;
  },

  // Get client stats
  getStats: async (): Promise<ClientStats> => {
    const response = await api.get('/clients/stats');
    return response.data.data;
  },

  // Get all tags
  getTags: async (): Promise<string[]> => {
    const response = await api.get('/clients/tags');
    return response.data.data.tags;
  },

  // Create client
  create: async (data: CreateClientData): Promise<Client> => {
    const response = await api.post('/clients', data);
    return response.data.data.client;
  },

  // Update client
  update: async (id: string, data: UpdateClientData): Promise<Client> => {
    const response = await api.patch(`/clients/${id}`, data);
    return response.data.data.client;
  },

  // Delete/archive client
  delete: async (id: string, permanent = false): Promise<void> => {
    await api.delete(`/clients/${id}`, { params: { permanent } });
  },

  // Add note
  addNote: async (id: string, content: string): Promise<ClientNote> => {
    const response = await api.post(`/clients/${id}/notes`, { content });
    return response.data.data.note;
  },

  // Delete note
  deleteNote: async (clientId: string, noteId: string): Promise<void> => {
    await api.delete(`/clients/${clientId}/notes/${noteId}`);
  },

  // Update tags
  updateTags: async (id: string, tags: string[]): Promise<Client> => {
    const response = await api.patch(`/clients/${id}/tags`, { tags });
    return response.data.data.client;
  },
};
